import { EngagementPeriod } from "@/types/engagement";

function nextDay(date: Date): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + 1);
  return result;
}

export function formatEventDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
}

export function periodToUtcRange(period: EngagementPeriod, now = new Date()): { from: string; to: string } {
  // Use UTC date parts to avoid local-midnight serialising as the previous UTC day in UTC+ zones
  const y = now.getUTCFullYear(), m = now.getUTCMonth(), d = now.getUTCDate();
  let from: Date;

  switch (period) {
    case EngagementPeriod.OneWeek:      from = new Date(Date.UTC(y, m, d - 7));  break;
    case EngagementPeriod.OneMonth:     from = new Date(Date.UTC(y, m - 1, d));  break;
    case EngagementPeriod.ThreeMonths:  from = new Date(Date.UTC(y, m - 3, d));  break;
    case EngagementPeriod.SixMonths:    from = new Date(Date.UTC(y, m - 6, d));  break;
    case EngagementPeriod.OneYear:      from = new Date(Date.UTC(y - 1, m, d));  break;
  }

  return { from: from.toISOString(), to: now.toISOString() };
}

export function startOfWeek(date: Date): Date {
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); // Monday-start
  return d;
}

export function groupKey(dateStr: string): string {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const thisWeekStart = startOfWeek(today);
  const lastWeekStart = new Date(thisWeekStart); lastWeekStart.setDate(thisWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(thisWeekStart); lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  const t = d.getTime();

  if (t === today.getTime()) return "__today__";
  if (t === yesterday.getTime()) return "__yesterday__";
  if (t >= thisWeekStart.getTime() && t < yesterday.getTime()) return "__this_week__";
  if (t >= lastWeekStart.getTime() && t <= lastWeekEnd.getTime()) return "__last_week__";
  if (t >= thisMonthStart.getTime() && t < thisWeekStart.getTime()) return "__this_month__";
  if (t >= lastMonthStart.getTime() && t <= lastMonthEnd.getTime()) return "__last_month__";
  return dateStr.slice(0, 7);
}

export function groupLabel(key: string): string {
  if (key === "__today__") return "Today";
  if (key === "__yesterday__") return "Yesterday";
  if (key === "__this_week__") return "Earlier this week";
  if (key === "__last_week__") return "Last week";
  if (key === "__this_month__") return "Earlier this month";
  if (key === "__last_month__") return "Last month";
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function groupDateRange(key: string): { from: string; to: string } {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const thisWeekStart = startOfWeek(today);
  const lastWeekStart = new Date(thisWeekStart); lastWeekStart.setDate(thisWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(thisWeekStart); lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  // "earlier this week" ends day before yesterday; "earlier this month" ends day before this week
  const dayBeforeYesterday = new Date(yesterday); dayBeforeYesterday.setDate(yesterday.getDate() - 1);
  const dayBeforeThisWeek = new Date(thisWeekStart); dayBeforeThisWeek.setDate(thisWeekStart.getDate() - 1);

  switch (key) {
    case "__today__": return { from: today.toISOString(), to: nextDay(today).toISOString() };
    case "__yesterday__": return { from: yesterday.toISOString(), to: today.toISOString() };
    case "__this_week__": return { from: thisWeekStart.toISOString(), to: nextDay(dayBeforeYesterday).toISOString() };
    case "__last_week__": return { from: lastWeekStart.toISOString(), to: nextDay(lastWeekEnd).toISOString() };
    case "__this_month__": return { from: thisMonthStart.toISOString(), to: nextDay(dayBeforeThisWeek).toISOString() };
    case "__last_month__": return { from: lastMonthStart.toISOString(), to: nextDay(lastMonthEnd).toISOString() };
    default: {
      const [year, month] = key.split("-").map(Number);
      return { from: new Date(year, month - 1, 1).toISOString(), to: new Date(year, month, 1).toISOString() };
    }
  }
}
